'use strict';
import isObject from 'lodash/isObject';
import {USER_COLLECTION} from './config';

export const USER_CLASS_NAME = 'UserDomain';

export function UserDomainProvider() {
  return {
    $get: [
      '$injector',
      '$log',
      'FirebaseUtil',
      '$firebaseObject',
      'AbstractDomainClass',
      '$http',
      'fetchUtils',
      'restUtils',
      'MetricMixin',
      'DeviceServices',
      'FirebaseQueueService',
      'ROOT_API_COLLECTION',
      'ROOT_API_SPEC',
      function (
        $injector,
        $log,
        FirebaseUtil,
        $firebaseObject,
        AbstractDomainClass,
        $http,
        fetchUtils,
        restUtils,
        MetricMixin,
        DeviceServices,
        FirebaseQueueService,
        ROOT_API_COLLECTION,
        ROOT_API_SPEC,
      ) {

        function UserDomain(data) {
          AbstractDomainClass.call(this, UserDomain, data);
        }

        AbstractDomainClass.__inheritFrom(UserDomain, USER_CLASS_NAME, USER_COLLECTION);

        UserDomain.$constraints.addConstraints({
          //uid: {type: 'string'},
          provider: {type: 'string'},
          displayName: {type: 'string'},
          email: {type: 'string'},
          contactData: {type: 'DomainLink', domainClass: 'PersonDomain', nullable: true},
          dateCreated: {type: 'Date'},
          lastUpdated: {type: 'Date'},

          /* Links and Collections */
          preferences: {type: 'LinkedDocument', domainClass: 'UserPreferenceDomain'},
          appState: {type: 'DomainCollection', domainClass: 'OrganizationDomain'},

          organizations: {type: 'DomainCollection', domainClass: 'OrganizationDomain'},
          projects: {type: 'DomainCollection', domainClass: 'ProjectDomain'},
          assetProjects: {type: 'DomainCollection', domainClass: 'AssetProjectDomain'},
          milestones: {type: 'DomainCollection', domainClass: 'MilestoneDomain'},
          invites: {type: 'DomainCollection', domainClass: 'UserInviteDomain', link: 'user'},

          priorities: {type: 'DomainCollection', domainClass: 'UserPinDomain', link: 'user', belongsTo: true},
          conversations: {type: 'DomainCollection', domainClass: 'ConversationDomain'},
          feed: {type: 'DomainCollection', domainClass: 'ActivityFeedDomain', link: 'feedable'},

          /* Private Data */
          privateProjects: {type: 'DomainCollection', domainClass: 'ProjectDomain'},
          privateTasks: {type: 'DomainCollection', domainClass: 'TaskDomain'},
          privateAssetTasks: {type: 'DomainCollection', domainClass: 'AssetTaskDomain'},

          /* Subscriptions */
          conversationsSubscriptions: {type: 'DomainCollection', domainClass: 'ConversationDomain'},

          /* User-oriented Metrics */
          conversationMetrics: {
            type: 'DomainCollection',
            domainClass: 'ConversationDomain',
            relationClass: 'UserProjectMetricsDomain'
          }, // TODO: fix relation class
          projectsMetrics: {
            type: 'DomainCollection',
            domainClass: 'ProjectDomain',
            relationClass: 'UserProjectMetricsDomain'
          },
          assetProjectsMetrics: {
            type: 'DomainCollection',
            domainClass: 'AssetProjectDomain',
            relationClass: 'UserProjectMetricsDomain'
          },
          tasksMetrics: {type: 'DomainCollection', domainClass: 'TaskDomain', relationClass: 'UserTaskMetricsDomain'},
          assetTasksMetrics: {
            type: 'DomainCollection',
            domainClass: 'AssetTaskDomain',
            relationClass: 'UserTaskMetricsDomain'
          },
          fileRequirementsMetrics: {
            type: 'DomainCollection',
            domainClass: 'FileRequirementDomain',
            relationClass: 'UserFileRequirementMetricsDomain'
          },
          assetFileRequirementsMetrics: {
            type: 'DomainCollection',
            domainClass: 'AssetFileRequirementDomain',
            relationClass: 'UserFileRequirementMetricsDomain'
          },
          fileAssetMetrics: {
            type: 'DomainCollection',
            domainClass: 'FileAssetDomain',
            relationClass: 'UserConversationMetricsDomain'
          }
        });

        MetricMixin(UserDomain);

        // TODO: Add per-file comment thread support for file-requirements version 2

        UserDomain.getOrganizationProfile = getOrganizationProfileStatic;
        UserDomain.getCommentsRef = getCommentsRefStatic;
        UserDomain.removeCommentsRef = removeCommentsRefStatic;
        UserDomain.addPushToken = addPushTokenStatic;
        UserDomain.removePushToken = removePushTokenStatic;
        UserDomain.addWebPushToken = addWebPushTokenStatic;
        UserDomain.removeWebPushToken = removeWebPushTokenStatic;
        UserDomain.updateLatestConversation = updateLatestConversationStatic;
        UserDomain.updateLatestPriority = updateLatestPriorityStatic;
        UserDomain.getCommentzReadCountRef = getCommentzReadCountRef;

        UserDomain.prototype.isMemberOfOrg = isMemberOfOrg;
        UserDomain.prototype.getOrganizationProfile = getOrganizationProfile;
        UserDomain.prototype.getProfile = getProfile;
        UserDomain.prototype.removeCommentsRef = removeCommentsRefProto;
        UserDomain.prototype.updateLatestConversation = updateLatestConversationProto;
        UserDomain.prototype.updateLatestPriority = updateLatestPriorityProto;
        UserDomain.prototype.updateEmailNotifications = updateEmailNotifications;
        UserDomain.prototype.addPushToken = addPushTokenProto;
        UserDomain.prototype.removePushToken = removePushTokenProto;
        UserDomain.prototype.addWebPushToken = addWebPushTokenProto;
        UserDomain.prototype.removeWebPushToken = removeWebPushTokenProto;
        UserDomain.prototype.toString = toString;

        function isMemberOfOrg(orgId) {
          return FirebaseUtil.read([
            UserDomain.COLLECTION + '-meta',
            this.$id,
            'links',
            'organizations',
            orgId
          ].join('/'))
            .then(relation => !!relation, () => false);
        }

        function getOrganizationProfileStatic(userId, orgId) {
          const PersonDomain = $injector.get('PersonDomain');
          return PersonDomain.getOrganizationProfile(userId, orgId);
        }

        function getOrganizationProfile(orgId) {
          return UserDomain.getOrganizationProfile(this.$id, orgId);
        }

        function getProfile() {
          return $injector.get('PersonDomain').$get(this.$id);
        }

        function getCommentsRefStatic(userId, conversationId) {
          return FirebaseUtil.firebaseRef([
            'users-meta',
            userId,
            'links',
            'conversation-metrics',
            conversationId,
            'readComments'
          ].join('/'))
        }

        function removeCommentsRefStatic(userId, conversationId) {
          return FirebaseUtil.firebaseRef([
            'users-meta',
            userId,
            'links',
            'conversation-metrics',
            conversationId
          ].join('/')).remove()
        }

        function removeCommentsRefProto(conversationId) {
          return removeCommentsRefStatic(this.$id, conversationId);
        }

        function updateLatestConversationProto() {
          return updateLatestConversationStatic(this.$id);
        }

        function updateLatestConversationStatic(userId) {
          return FirebaseUtil.set([
            'users-metrics',
            userId,
            'data',
            'latestConversation',
            'isRead'
          ].join('/'), true)
        }

        function updateLatestPriorityProto() {
          return updateLatestConversationStatic(this.$id);
        }

        function updateLatestPriorityStatic(userId) {
          return FirebaseUtil.set([
            'users-metrics',
            userId,
            'data',
            'latestPriority',
            'isRead'
          ].join('/'), true)
        }

        function _getReadCommentsPath(userId, conversationId) {
          return [
            'users-meta',
            userId,
            'links',
            'conversation-metrics',
            conversationId,
            'readComments'
          ].join('/');
        }

        function getCommentzReadCountRef(userId, conversationId) {
          return FirebaseUtil.firebaseRef(_getReadCommentsPath(userId, conversationId));
        }

        function updateEmailNotifications(enable) {
          const url = `${fetchUtils.API_URL}/api/users/${this.$id}/notifications/email`;
          const request = {
            method: enable ? 'PUT' : 'DELETE',
            mode: 'cors',
            cache: 'default'
          };

          return fetchUtils.requestWithAuth(url, request);
        }

        function addPushTokenProto(token) {
          return addPushTokenStatic(this.$id, token)
        }

        function addPushTokenStatic(userId, token) { // supports Ionic.io push user API
          const request = {
            method: 'users/notifications/mobile-push/tokens/add',
            token: isObject(token) ? (token.token ? token.token : token._token) : token,
            device: DeviceServices.getDeviceType()
          };

          return FirebaseQueueService.addTask(request, userId, ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function removePushTokenProto(token) {
          return removePushTokenStatic(this.$id, token)
        }

        function removePushTokenStatic(userId, token) { // supports Ionic.io push user API
          const request = {
            method: 'users/notifications/mobile-push/tokens/remove',
            token: isObject(token) && token._token ? token._token : token,
            device: DeviceServices.getDeviceType()
          };

          return FirebaseQueueService.addTask(request, userId, ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function addWebPushTokenProto(token) {
          return addWebPushTokenStatic(this.$id, token)
        }

        function addWebPushTokenStatic(userId, subscription) { // supports web push user API
          const request = {
            method: 'users/notifications/web-push/tokens/add',
            subscription: subscription
            // endpoint: data.endpoint,
            // key: data.key,
            // authSecret: data.authSecret
          };

          return FirebaseQueueService.addTask(request, userId, ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function removeWebPushTokenProto(token) {
          return removeWebPushTokenStatic(this.$id, token)
        }

        function removeWebPushTokenStatic(userId, endpoint) { // supports web push user API
          const request = {
            method: 'users/notifications/web-push/tokens/remove',
            endpoint: endpoint
          };

          return FirebaseQueueService.addTask(request, userId, ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        function toString() {
          return this.displayName;
        }

        return UserDomain;
      }]
  };
}



// WEBPACK FOOTER //
// ./src/js/auth/domain/UserDomain.js